/**
 * QuickNavigationFAB - Floating Action Button for quick section navigation
 * Demo/Mockup version - shows concept for quick access to page sections
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  Sheet,
  Typography,
  Tooltip,
} from '@mui/joy';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Article as DefaultIcon,
} from '@mui/icons-material';
import { useIsMobile } from '../hooks/useIsMobile';

export interface NavigationSection {
  id: string;
  label: string;
  icon?: React.ReactElement;
  scrollToId?: string; // Element ID to scroll to
  onClick?: () => void; // Custom action
}

interface QuickNavigationFABProps {
  sections: NavigationSection[];
  mobileSections?: NavigationSection[]; // Optional mobile-specific sections
  position?: 'bottom-right' | 'bottom-left';
}

export default function QuickNavigationFAB({
  sections,
  mobileSections,
  position = 'bottom-right',
}: QuickNavigationFABProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useIsMobile();

  // Use mobile sections if provided and on mobile, otherwise use default sections
  const activeSections = isMobile && mobileSections ? mobileSections : sections;

  const handleSectionClick = (section: NavigationSection) => {
    if (section.onClick) {
      section.onClick();
    } else if (section.scrollToId) {
      const element = document.getElementById(section.scrollToId);
      if (element) {
        // Smooth scroll with offset for fixed header
        const yOffset = -100; // Adjust based on your header height
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  // Position styles - add extra bottom padding on mobile to avoid bottom nav bar (60px height + 24px spacing)
  const positionStyles = position === 'bottom-right'
    ? { bottom: isMobile ? 84 : 24, right: 24 }
    : { bottom: isMobile ? 84 : 24, left: 24 };

  return (
    <>
      {/* Floating Action Button */}
      <Tooltip 
        title="Quick Navigation" 
        placement={position === 'bottom-right' ? 'left' : 'right'}
        variant="soft"
      >
        <IconButton
          size="lg"
          color="primary"
          variant="solid"
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            position: 'fixed',
            ...positionStyles,
            zIndex: 9999,
            width: 56,
            height: 56,
            borderRadius: '50%',
            boxShadow: 'lg',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: 'xl',
            },
            ...(isOpen && {
              transform: 'rotate(180deg)',
            }),
          }}
        >
          {isOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Tooltip>

      {/* Navigation Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <Box
            onClick={() => setIsOpen(false)}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.25)',
              zIndex: 9998,
              backdropFilter: 'blur(4px)',
            }}
          />

          {/* Menu Sheet */}
          <Sheet
            variant="outlined"
            sx={{
              position: 'fixed',
              ...(position === 'bottom-right' 
                ? { bottom: isMobile ? 156 : 96, right: 24 }
                : { bottom: isMobile ? 156 : 96, left: 24 }
              ),
              zIndex: 9999,
              maxWidth: isMobile ? 'calc(100vw - 48px)' : 320,
              maxHeight: 'calc(100vh - 150px)',
              overflowY: 'auto',
              borderRadius: 'lg',
              boxShadow: 'xl',
              animation: 'slideUp 0.2s ease-out',
              '@keyframes slideUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(20px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography level="title-md" sx={{ fontWeight: 600 }}>
                Jump to Section
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Quick access to page sections
              </Typography>
            </Box>

            {/* Navigation List */}
            <List sx={{ py: 1 }}>
              {activeSections.map((section, index) => (
                <ListItem key={section.id}>
                  <ListItemButton
                    onClick={() => handleSectionClick(section)}
                    sx={{
                      borderRadius: 'md',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemDecorator>
                      {section.icon || <DefaultIcon />}
                    </ListItemDecorator>
                    <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                      {section.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Sheet>
        </>
      )}
    </>
  );
}
